<script setup>
import { ref, reactive, watch } from "vue";
import { querySupplyStandard, adjustSupplyStandard } from "@/api/effluent";
import { ElMessage } from "element-plus";

const props = defineProps({
  pageParams: {
    type: Object,
  },
});

const emits = defineEmits(["afterSubmit"]);

// 表单ref
const ruleRef = ref(null);

// 页面loading
const loading = ref(false);

const formData = ref({
  id: undefined,
  phStart: undefined,
  phEnd: undefined,
  waterTemperature: undefined,
  totalColiform: undefined,
  escherichiaColi: undefined,
  totalBacterial: undefined,
  chroma: undefined,
  turbidity: undefined,
  oao: undefined,
  visible: undefined,
  permanganate: undefined,
  ammonia: undefined,
  freeChlorineLimit: undefined,
  freeChlorineOutSurplus: undefined,
  freeChlorineEndSurplus: undefined,
  chlorineDioxideLimit: undefined,
  chlorineDioxideOutSurplus: undefined,
  chlorineDioxideEndSurplus: undefined,
});

// 自定义表单校验
const floatReg = /^(([1-9]{1}[0-9]{0,9})|(0{1}))(\.\d{1,2})?$/;
const validatePosNumber = (rule, value, callback) => {
  if (rule.field != "ph") {
    if (floatReg.test(value)) {
      callback();
    } else {
      callback(new Error("输入内容必须是正数且只能保留两位小数"));
    }
  }
};
const validateMergeNum = (rule, value, callback) => {
  if (rule.field == "ph") {
    const num1 = formData.value.phStart;
    const num2 = formData.value.phEnd;
    if (Number(num1) <= Number(num2)) {
      callback("上限不能小于等于下限!");
    } else if (floatReg.test(num1) && floatReg.test(num2)) {
      callback();
    } else if (num1 === "" || num1 === null || num2 === "" || num2 === null) {
      callback();
    } else {
      callback(new Error("输入内容必须是正数且只能保留两位小数"));
    }
  }
};

const rules = reactive({
  ph: [{ validator: validateMergeNum, trigger: "blur" }],
  waterTemperature: [{ validator: validatePosNumber, trigger: "blur" }],
  totalColiform: [{ validator: validatePosNumber, trigger: "blur" }],
  escherichiaColi: [{ validator: validatePosNumber, trigger: "blur" }],
  totalBacterial: [{ validator: validatePosNumber, trigger: "blur" }],
  chroma: [{ validator: validatePosNumber, trigger: "blur" }],
  turbidity: [{ validator: validatePosNumber, trigger: "blur" }],
  oao: [{ validator: validatePosNumber, trigger: "blur" }],
  visible: [{ validator: validatePosNumber, trigger: "blur" }],
  permanganate: [{ validator: validatePosNumber, trigger: "blur" }],
  ammonia: [{ validator: validatePosNumber, trigger: "blur" }],
  freeChlorineLimit: [{ validator: validatePosNumber, trigger: "blur" }],
  freeChlorineOutSurplus: [{ validator: validatePosNumber, trigger: "blur" }],
  freeChlorineEndSurplus: [{ validator: validatePosNumber, trigger: "blur" }],
  chlorineDioxideLimit: [{ validator: validatePosNumber, trigger: "blur" }],
  chlorineDioxideOutSurplus: [
    { validator: validatePosNumber, trigger: "blur" },
  ],
  chlorineDioxideEndSurplus: [
    { validator: validatePosNumber, trigger: "blur" },
  ],
});

// 获取数据
const getData = () => {
  loading.value = true;
  querySupplyStandard()
    .then((res) => {
      if (JSON.stringify(res.data) === "{}") {
        formData.value = {
          id: undefined,
          phStart: undefined,
          phEnd: undefined,
          waterTemperature: undefined,
          totalColiform: undefined,
          escherichiaColi: undefined,
          totalBacterial: undefined,
          chroma: undefined,
          turbidity: undefined,
          oao: undefined,
          visible: undefined,
          permanganate: undefined,
          ammonia: undefined,
          freeChlorineLimit: undefined,
          freeChlorineOutSurplus: undefined,
          freeChlorineEndSurplus: undefined,
          chlorineDioxideLimit: undefined,
          chlorineDioxideOutSurplus: undefined,
          chlorineDioxideEndSurplus: undefined,
        };
      } else {
        formData.value = res.data;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

// 保存
const submitForm = () => {
  ruleRef.value?.validate((valid) => {
    if (valid) {
      adjustSupplyStandard(formData.value).then((res) => {
        if (res.code == "2000") {
          ElMessage.success("操作成功");
          emits("afterSubmit");
        }
      });
    }
  });
};

watch(
  () => props.pageParams,
  (newVal) => {
    if (newVal) {
      formData.value.type = newVal.type;
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

defineExpose({
  getData,
  submitForm,
});
</script>

<template>
  <el-form
    ref="ruleRef"
    :model="formData"
    :rules="rules"
    label-position="top"
    v-loading="loading"
  >
    <el-row :gutter="10">
      <el-col :span="8">
        <el-form-item label="pH" prop="ph">
          <el-row :gutter="10">
            <el-col :span="12">
              <el-input placeholder="请输入" v-model="formData.phStart">
                <template #prepend>上限:</template>
              </el-input>
            </el-col>
            <el-col :span="12">
              <el-input placeholder="请输入" v-model="formData.phEnd">
                <template #prepend>下限:</template>
              </el-input>
            </el-col>
          </el-row>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="水温" prop="waterTemperature">
          <el-input placeholder="请输入" v-model="formData.waterTemperature">
            <template #append>℃</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="总大肠菌群" prop="totalColiform">
          <el-input placeholder="请输入" v-model="formData.totalColiform">
            <template #append>MPN/100mL</template>
          </el-input>
        </el-form-item>
      </el-col>
    </el-row>
    <el-row :gutter="10">
      <el-col :span="8">
        <el-form-item label="大肠埃希氏菌" prop="escherichiaColi">
          <el-input placeholder="请输入" v-model="formData.escherichiaColi">
            <template #append>MPN/100mL</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="菌落总数" prop="totalBacterial">
          <el-input placeholder="请输入" v-model="formData.totalBacterial">
            <template #append>MPN/100mL</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="色度" prop="chroma">
          <el-input placeholder="请输入" v-model="formData.chroma">
            <template #append>度</template>
          </el-input>
        </el-form-item>
      </el-col>
    </el-row>
    <el-row :gutter="10">
      <el-col :span="8">
        <el-form-item label="浑浊度" prop="turbidity">
          <el-input placeholder="请输入" v-model="formData.turbidity">
            <template #append>NTU</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="臭和味" prop="oao">
          <el-input placeholder="请输入" v-model="formData.oao"> </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="肉眼可见物" prop="visible">
          <el-input placeholder="请输入" v-model="formData.visible"> </el-input>
        </el-form-item>
      </el-col>
    </el-row>
    <el-row :gutter="10">
      <el-col :span="8">
        <el-form-item label="高锰酸盐指数" prop="permanganate">
          <el-input placeholder="请输入" v-model="formData.permanganate">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="氨(以N计)" prop="ammonia">
          <el-input placeholder="请输入" v-model="formData.ammonia">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
    </el-row>
    <div class="my_line_box">
      <el-divider direction="vertical" class="my_line"></el-divider>
      <span class="my_line_txt">游离氯</span>
    </div>
    <el-row :gutter="10">
      <el-col :span="8">
        <el-form-item label="出厂水和末梢水限值" prop="freeChlorineLimit">
          <el-input placeholder="请输入" v-model="formData.freeChlorineLimit">
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="出厂水余量" prop="freeChlorineOutSurplus">
          <el-input
            placeholder="请输入"
            v-model="formData.freeChlorineOutSurplus"
          >
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="末梢水余量" prop="freeChlorineEndSurplus">
          <el-input
            placeholder="请输入"
            v-model="formData.freeChlorineEndSurplus"
          >
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
    </el-row>
    <div class="my_line_box">
      <el-divider direction="vertical" class="my_line"></el-divider>
      <span class="my_line_txt">二氧化氯</span>
    </div>
    <el-row :gutter="10">
      <el-col :span="8">
        <el-form-item label="出厂水和末梢水限值" prop="chlorineDioxideLimit">
          <el-input
            placeholder="请输入"
            v-model="formData.chlorineDioxideLimit"
          >
            <template#append>mg/L</template#append>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="出厂水余量" prop="chlorineDioxideOutSurplus">
          <el-input
            placeholder="请输入"
            v-model="formData.chlorineDioxideOutSurplus"
          >
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
      <el-col :span="8">
        <el-form-item label="末梢水余量" prop="chlorineDioxideEndSurplus">
          <el-input
            placeholder="请输入"
            v-model="formData.chlorineDioxideEndSurplus"
          >
            <template #append>mg/L</template>
          </el-input>
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>

<style lang="scss" scoped>
.my_line_box {
  display: flex;
  align-items: center;
  margin-top: 10px;
  .my_line {
    background-color: #3f78f7;
    width: 4px;
    height: 14px;
  }
  .my_line_txt {
    font-family: "PingFang SC";
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    text-align: justify;
    line-height: 14px;
  }
}
</style>
